import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  AlertTriangle,
  ArrowUpRight,
  BadgeDollarSign,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Download,
  FileSpreadsheet,
  Filter,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';
import { formatMoney } from '../i18n/orgFormat';

type RevenueSummary = {
  invoiceCount: number;
  openInvoiceCount: number;
  totalBilled: number;
  totalCollected: number;
  totalOutstanding: number;
  overdueBalance: number;
  averageDaysInAr: number;
  collectionRate: number;
  collectionsLast30Days: number;
  billedLast30Days: number;
  medianRisk: number;
};

type RevenueRow = {
  id: string;
  invoiceNumber: string;
  patientId: string;
  patient: string;
  mrn: string;
  payer: string;
  mix: 'commercial' | 'medicare' | 'medicaid' | 'self-pay' | 'other';
  providerId: string | null;
  provider: string;
  serviceType: string;
  billed: number;
  paid: number;
  balance: number;
  status: 'UNPAID' | 'PENDING' | 'PAID' | 'REFUNDED';
  ageDays: number;
  pastDueDays: number;
  riskScore: number;
  riskBand: 'low' | 'medium' | 'high';
  collectionProbability: number;
  forecastDate: string | null;
  invoiceDate: string;
  dueDate: string | null;
  paymentCount: number;
  lastPaymentDate: string | null;
  paymentMethods: string[];
  itemCount: number;
};

type RevenueInsightsPayload = {
  generatedAt: string;
  filters: {
    months: number;
    openOnly: boolean;
    payer: string | null;
    providerId: string | null;
  };
  summary: RevenueSummary;
  charts: {
    monthlyPerformance: Array<{ key: string; label: string; billed: number; collected: number; outstanding: number }>;
    payerMix: Array<{ payer: string; mix: string; label: string; billed: number; collected: number; outstanding: number; invoices: number }>;
    aging: Array<{ key: string; label: string; amount: number; invoices: number }>;
    providerPerformance: Array<{ providerId: string | null; provider: string; billed: number; collected: number; outstanding: number; invoices: number }>;
    statusBreakdown: Array<{ status: string; count: number; total: number; outstanding: number }>;
  };
  options: {
    payers: string[];
    providers: Array<{ id: string; name: string }>;
  };
  rows: RevenueRow[];
};

const mixTone: Record<RevenueRow['mix'], string> = {
  commercial: 'bg-sky-50 text-sky-700',
  medicare: 'bg-rose-50 text-rose-700',
  medicaid: 'bg-emerald-50 text-emerald-700',
  'self-pay': 'bg-amber-50 text-amber-700',
  other: 'bg-slate-100 text-slate-700',
};

// Display-only labels. The underlying `mix` keys stay aligned with the backend
// payload; we only relabel the user-visible chip to LATAM-neutral terminology.
const mixLabel: Record<RevenueRow['mix'], string> = {
  commercial: 'Insurance',
  medicare: 'Insurance',
  medicaid: 'Insurance',
  'self-pay': 'Particular',
  other: 'Other',
};

const riskTone: Record<RevenueRow['riskBand'], string> = {
  low: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  medium: 'bg-amber-50 text-amber-700 border-amber-100',
  high: 'bg-rose-50 text-rose-700 border-rose-100',
};

const statusTone: Record<RevenueRow['status'], string> = {
  UNPAID: 'bg-rose-50 text-rose-700 border-rose-100',
  PENDING: 'bg-amber-50 text-amber-700 border-amber-100',
  PAID: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  REFUNDED: 'bg-slate-100 text-slate-600 border-slate-200',
};

function formatCurrency(value: number) {
  return formatMoney(value, { maximumFractionDigits: 0 });
}

function formatCurrencyCompact(value: number) {
  return formatMoney(value, { notation: 'compact', maximumFractionDigits: 1 });
}

function formatPercent(value: number) {
  return `${Math.round((value || 0) * 100)}%`;
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function downloadBlob(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(objectUrl);
}

function toCsv(rows: RevenueRow[]) {
  const columns = [
    'Invoice',
    'Patient',
    'MRN',
    'Payer',
    'Provider',
    'Service',
    'Billed',
    'Paid',
    'Balance',
    'Status',
    'AgeDays',
    'PastDueDays',
    'RiskScore',
    'CollectionProbability',
    'ForecastDate',
    'InvoiceDate',
    'DueDate',
    'PaymentCount',
    'PaymentMethods',
  ];
  const lines = [
    columns.join(','),
    ...rows.map((row) =>
      [
        row.invoiceNumber,
        row.patient,
        row.mrn,
        row.payer,
        row.provider,
        row.serviceType,
        row.billed,
        row.paid,
        row.balance,
        row.status,
        row.ageDays,
        row.pastDueDays,
        row.riskScore,
        row.collectionProbability,
        row.forecastDate || '',
        row.invoiceDate,
        row.dueDate || '',
        row.paymentCount,
        row.paymentMethods.join(' | '),
      ]
        .map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`)
        .join(','),
    ),
  ];
  return lines.join('\n');
}

function chartBase(title: string): Highcharts.Options {
  return {
    chart: {
      backgroundColor: 'transparent',
      style: {
        fontFamily: 'inherit',
      },
    },
    title: {
      text: title,
      align: 'left',
      style: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#0f172a',
      },
    },
    credits: { enabled: false },
    legend: {
      itemStyle: {
        color: '#475569',
      },
    },
    xAxis: {
      labels: {
        style: {
          color: '#64748b',
        },
      },
      lineColor: '#e2e8f0',
      tickColor: '#e2e8f0',
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: '#e2e8f0',
      labels: {
        style: {
          color: '#64748b',
        },
      },
    },
    tooltip: {
      shared: true,
      borderRadius: 12,
      backgroundColor: '#0f172a',
      style: {
        color: '#f8fafc',
      },
    },
  };
}

export default function RevenueInsightsDashboard() {
  const { fetchJson } = useApiClient();
  const { tr } = useI18n();

  const [data, setData] = useState<RevenueInsightsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [months, setMonths] = useState(6);
  const [openOnly, setOpenOnly] = useState(false);
  const [payer, setPayer] = useState('');
  const [providerId, setProviderId] = useState('');
  const [search, setSearch] = useState('');

  const loadData = useCallback(
    async (soft = false) => {
      if (soft) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('months', String(months));
        params.set('openOnly', String(openOnly));
        if (payer) params.set('payer', payer);
        if (providerId) params.set('providerId', providerId);
        const payload = await fetchJson<RevenueInsightsPayload>(`/billing/revenue-insights?${params.toString()}`);
        setData(payload);
      } catch (err: any) {
        setError(err?.message || tr('Failed to load revenue insights.'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [fetchJson, months, openOnly, payer, providerId, tr],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const visibleRows = useMemo(() => {
    if (!data) return [];
    const query = search.trim().toLowerCase();
    return data.rows.filter((row) => {
      if (!query) return true;
      return (
        row.invoiceNumber.toLowerCase().includes(query) ||
        row.patient.toLowerCase().includes(query) ||
        row.mrn.toLowerCase().includes(query) ||
        row.payer.toLowerCase().includes(query) ||
        row.provider.toLowerCase().includes(query) ||
        row.serviceType.toLowerCase().includes(query)
      );
    });
  }, [data, search]);

  useEffect(() => {
    if (!visibleRows.length) {
      setSelectedId('');
      return;
    }
    setSelectedId((current) => (current && visibleRows.some((row) => row.id === current) ? current : visibleRows[0].id));
  }, [visibleRows]);

  const selectedRow = useMemo(
    () => visibleRows.find((row) => row.id === selectedId) ?? data?.rows.find((row) => row.id === selectedId) ?? null,
    [data?.rows, selectedId, visibleRows],
  );

  const monthlyOptions = useMemo<Highcharts.Options>(() => {
    if (!data) return chartBase('Monthly cash performance');
    return {
      ...chartBase('Monthly cash performance'),
      chart: { ...(chartBase('Monthly cash performance').chart || {}), type: 'areaspline', height: 320 },
      xAxis: {
        ...(chartBase('Monthly cash performance').xAxis as Highcharts.XAxisOptions),
        categories: data.charts.monthlyPerformance.map((point) => point.label),
      },
      series: [
        { name: 'Billed', type: 'areaspline', data: data.charts.monthlyPerformance.map((point) => point.billed), color: '#0f766e' },
        { name: 'Collected', type: 'areaspline', data: data.charts.monthlyPerformance.map((point) => point.collected), color: '#2563eb' },
        { name: 'Outstanding', type: 'areaspline', data: data.charts.monthlyPerformance.map((point) => point.outstanding), color: '#dc2626' },
      ],
    };
  }, [data]);

  const payerMixOptions = useMemo<Highcharts.Options>(() => {
    if (!data) return chartBase('Payer mix');
    return {
      ...chartBase('Payer mix by outstanding balance'),
      chart: { ...(chartBase('Payer mix by outstanding balance').chart || {}), type: 'pie', height: 320 },
      tooltip: {
        pointFormat: '<b>{point.y:,.0f}</b> outstanding<br/>{point.percentage:.1f}% share',
      },
      plotOptions: {
        pie: {
          innerSize: '62%',
          dataLabels: {
            enabled: true,
            format: '{point.name}',
          },
        },
      },
      series: [
        {
          type: 'pie',
          name: 'Outstanding',
          data: data.charts.payerMix.map((entry) => ({
            name: entry.payer,
            y: entry.outstanding,
          })),
        },
      ],
    };
  }, [data]);

  const agingOptions = useMemo<Highcharts.Options>(() => {
    if (!data) return chartBase('AR aging');
    return {
      ...chartBase('AR aging buckets'),
      chart: { ...(chartBase('AR aging buckets').chart || {}), type: 'column', height: 320 },
      xAxis: {
        ...(chartBase('AR aging buckets').xAxis as Highcharts.XAxisOptions),
        categories: data.charts.aging.map((entry) => entry.label),
      },
      series: [
        {
          type: 'column',
          name: 'Outstanding balance',
          data: data.charts.aging.map((entry) => entry.amount),
          color: '#f97316',
        },
      ],
    };
  }, [data]);

  const providerOptions = useMemo<Highcharts.Options>(() => {
    if (!data) return chartBase('Provider performance');
    return {
      ...chartBase('Provider revenue pressure'),
      chart: { ...(chartBase('Provider revenue pressure').chart || {}), type: 'bar', height: 320 },
      xAxis: {
        ...(chartBase('Provider revenue pressure').xAxis as Highcharts.XAxisOptions),
        categories: data.charts.providerPerformance.map((entry) => entry.provider),
      },
      series: [
        {
          type: 'bar',
          name: 'Outstanding',
          data: data.charts.providerPerformance.map((entry) => entry.outstanding),
          color: '#b91c1c',
        },
        {
          type: 'bar',
          name: 'Collected',
          data: data.charts.providerPerformance.map((entry) => entry.collected),
          color: '#15803d',
        },
      ],
    };
  }, [data]);

  async function exportCsv() {
    if (!visibleRows.length) return;
    setExporting('csv');
    try {
      const csv = toCsv(visibleRows);
      downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `revenue-insights-${new Date().toISOString().slice(0, 10)}.csv`);
    } finally {
      setExporting(null);
    }
  }

  async function exportWorkbook() {
    if (!data) return;
    setExporting('xlsx');
    try {
      const XLSX = await import('xlsx');
      const workbook = XLSX.utils.book_new();
      const summarySheet = XLSX.utils.json_to_sheet([
        { Metric: 'Invoice count', Value: data.summary.invoiceCount },
        { Metric: 'Open invoice count', Value: data.summary.openInvoiceCount },
        { Metric: 'Total billed', Value: data.summary.totalBilled },
        { Metric: 'Total collected', Value: data.summary.totalCollected },
        { Metric: 'Total outstanding', Value: data.summary.totalOutstanding },
        { Metric: 'Overdue balance', Value: data.summary.overdueBalance },
        { Metric: 'Average days in AR', Value: data.summary.averageDaysInAr },
        { Metric: 'Collection rate', Value: data.summary.collectionRate },
        { Metric: 'Collections last 30 days', Value: data.summary.collectionsLast30Days },
        { Metric: 'Billed last 30 days', Value: data.summary.billedLast30Days },
        { Metric: 'Median risk', Value: data.summary.medianRisk },
      ]);
      const invoiceSheet = XLSX.utils.json_to_sheet(
        visibleRows.map((row) => ({
          Invoice: row.invoiceNumber,
          Patient: row.patient,
          MRN: row.mrn,
          Payer: row.payer,
          Provider: row.provider,
          Service: row.serviceType,
          Billed: row.billed,
          Paid: row.paid,
          Balance: row.balance,
          Status: row.status,
          AgeDays: row.ageDays,
          PastDueDays: row.pastDueDays,
          RiskScore: row.riskScore,
          CollectionProbability: row.collectionProbability,
          ForecastDate: row.forecastDate ? new Date(row.forecastDate).toLocaleDateString() : '',
          InvoiceDate: new Date(row.invoiceDate).toLocaleDateString(),
          DueDate: row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '',
          PaymentCount: row.paymentCount,
          PaymentMethods: row.paymentMethods.join(', '),
        })),
      );
      const trendSheet = XLSX.utils.json_to_sheet(
        data.charts.monthlyPerformance.map((point) => ({
          Month: point.label,
          Billed: point.billed,
          Collected: point.collected,
          Outstanding: point.outstanding,
        })),
      );

      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
      XLSX.utils.book_append_sheet(workbook, invoiceSheet, 'Invoices');
      XLSX.utils.book_append_sheet(workbook, trendSheet, 'Monthly Trend');
      XLSX.writeFile(workbook, `revenue-insights-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } finally {
      setExporting(null);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-wide text-slate-400">{tr('Financial')}</p>
          <h1 className="text-2xl font-semibold text-ink">{tr('Revenue Insights Dashboard')}</h1>
          <p className="max-w-3xl text-sm text-slate-500">
            {tr('Real billing performance across open AR, recent collections, payer mix, provider pressure, and invoice-level recovery risk.')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => loadData(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300"
            disabled={refreshing}
          >
            {refreshing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            {tr('Refresh')}
          </button>
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300"
            disabled={!visibleRows.length || exporting !== null}
          >
            {exporting === 'csv' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            {tr('Export CSV')}
          </button>
          <button
            type="button"
            onClick={exportWorkbook}
            className="inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2 text-sm font-semibold text-white shadow-soft disabled:opacity-60"
            disabled={!data || exporting !== null}
          >
            {exporting === 'xlsx' ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />}
            {tr('Export Excel')}
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_280px_280px_220px]">
        <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
              <BadgeDollarSign size={20} />
            </div>
            <div>
              <div className="text-sm text-slate-500">{tr('Open A/R')}</div>
              <div className="mt-1 text-2xl font-semibold text-ink">{formatCurrencyCompact(data?.summary.totalOutstanding || 0)}</div>
              <div className="mt-1 text-xs text-slate-500">
                {(data?.summary.openInvoiceCount || 0).toLocaleString()} {tr('open invoices')}
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50 text-sky-700">
              <CheckCircle2 size={20} />
            </div>
            <div>
              <div className="text-sm text-slate-500">{tr('Collections 30d')}</div>
              <div className="mt-1 text-2xl font-semibold text-ink">{formatCurrencyCompact(data?.summary.collectionsLast30Days || 0)}</div>
              <div className="mt-1 text-xs text-slate-500">{formatPercent(data?.summary.collectionRate || 0)} {tr('realized overall')}</div>
            </div>
          </div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
              <AlertTriangle size={20} />
            </div>
            <div>
              <div className="text-sm text-slate-500">{tr('Overdue balance')}</div>
              <div className="mt-1 text-2xl font-semibold text-ink">{formatCurrencyCompact(data?.summary.overdueBalance || 0)}</div>
              <div className="mt-1 text-xs text-slate-500">{formatPercent(data?.summary.medianRisk || 0)} {tr('median recovery risk')}</div>
            </div>
          </div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
              <Clock3 size={20} />
            </div>
            <div>
              <div className="text-sm text-slate-500">{tr('Average days in AR')}</div>
              <div className="mt-1 text-2xl font-semibold text-ink">{data?.summary.averageDaysInAr || 0}</div>
              <div className="mt-1 text-xs text-slate-500">{formatCurrencyCompact(data?.summary.totalBilled || 0)} {tr('billed in range')}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-ink">{tr('Filters')}</h2>
            <p className="text-sm text-slate-500">{tr('Slice the dashboard by lookback window, payer, provider, and open-only AR.')}</p>
          </div>
          <div className="text-xs text-slate-400">
            {data?.generatedAt ? `${tr('Updated')} ${new Date(data.generatedAt).toLocaleString()}` : ' '}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-[180px_180px_220px_240px_minmax(0,1fr)]">
          <label className="block text-sm">
            <span className="mb-1.5 flex items-center gap-2 font-medium text-slate-700">
              <Filter size={14} />
              {tr('Lookback')}
            </span>
            <select
              value={months}
              onChange={(event) => setMonths(Number(event.target.value))}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-300"
            >
              <option value={3}>{tr('3 months')}</option>
              <option value={6}>{tr('6 months')}</option>
              <option value={12}>{tr('12 months')}</option>
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1.5 flex items-center gap-2 font-medium text-slate-700">
              <CalendarClock size={14} />
              {tr('Ledger scope')}
            </span>
            <select
              value={openOnly ? 'open' : 'all'}
              onChange={(event) => setOpenOnly(event.target.value === 'open')}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-300"
            >
              <option value="all">{tr('All invoices')}</option>
              <option value="open">{tr('Open AR only')}</option>
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1.5 block font-medium text-slate-700">{tr('Payer')}</span>
            <select
              value={payer}
              onChange={(event) => setPayer(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-300"
            >
              <option value="">{tr('All payers')}</option>
              {data?.options.payers.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1.5 block font-medium text-slate-700">{tr('Provider')}</span>
            <select
              value={providerId}
              onChange={(event) => setProviderId(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-300"
            >
              <option value="">{tr('All providers')}</option>
              {data?.options.providers.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1.5 flex items-center gap-2 font-medium text-slate-700">
              <Search size={14} />
              {tr('Search visible ledger')}
            </span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={tr('Invoice, patient, payer, provider...')}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-300"
            />
          </label>
        </div>
      </section>

      {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
          <span className="inline-flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            {tr('Loading revenue insights...')}
          </span>
        </div>
      ) : !data ? (
        <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
          {tr('No revenue data is available yet.')}
        </div>
      ) : (
        <>
          <section className="grid grid-cols-1 gap-5 2xl:grid-cols-2">
            <ChartCard title={tr('Monthly cash performance')}>
              <HighchartsReact highcharts={Highcharts} options={monthlyOptions} />
            </ChartCard>
            <ChartCard title={tr('Payer mix by outstanding balance')}>
              <HighchartsReact highcharts={Highcharts} options={payerMixOptions} />
            </ChartCard>
            <ChartCard title={tr('AR aging buckets')}>
              <HighchartsReact highcharts={Highcharts} options={agingOptions} />
            </ChartCard>
            <ChartCard title={tr('Provider revenue pressure')}>
              <HighchartsReact highcharts={Highcharts} options={providerOptions} />
            </ChartCard>
          </section>

          <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[minmax(0,1fr)_340px]">
            <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-ink">{tr('Invoice recovery board')}</h2>
                  <p className="text-sm text-slate-500">
                    {visibleRows.length.toLocaleString()} {tr('rows visible after filters and search')}
                  </p>
                </div>
                <div className="text-xs text-slate-400">{tr('Sorted by highest outstanding balance and risk')}</div>
              </div>

              <div className="max-h-[760px] overflow-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 border-b border-slate-100 bg-white text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-6 py-3">{tr('Invoice')}</th>
                      <th className="px-4 py-3">{tr('Patient')}</th>
                      <th className="px-4 py-3">{tr('Payer')}</th>
                      <th className="px-4 py-3">{tr('Provider')}</th>
                      <th className="px-4 py-3">{tr('Service')}</th>
                      <th className="px-4 py-3 text-right">{tr('Billed')}</th>
                      <th className="px-4 py-3 text-right">{tr('Paid')}</th>
                      <th className="px-4 py-3 text-right">{tr('Balance')}</th>
                      <th className="px-4 py-3 text-center">{tr('Days')}</th>
                      <th className="px-4 py-3">{tr('Risk')}</th>
                      <th className="px-4 py-3">{tr('Forecast')}</th>
                      <th className="px-6 py-3">{tr('Status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visibleRows.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="px-6 py-10 text-center text-sm text-slate-500">
                          {tr('No invoices match the selected filters.')}
                        </td>
                      </tr>
                    ) : (
                      visibleRows.map((row) => {
                        const selected = row.id === selectedRow?.id;
                        return (
                          <tr
                            key={row.id}
                            onClick={() => setSelectedId(row.id)}
                            className={`cursor-pointer transition ${selected ? 'bg-sky-50/70' : 'hover:bg-slate-50'}`}
                          >
                            <td className="px-6 py-4">
                              <div className="font-semibold text-ink">{row.invoiceNumber}</div>
                              <div className="text-[11px] text-slate-400">{formatDate(row.invoiceDate)}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="font-medium text-ink">{row.patient}</div>
                              <div className="text-xs text-slate-500">{row.mrn}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="font-medium text-ink">{row.payer}</div>
                              <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${mixTone[row.mix]}`}>
                                {tr(mixLabel[row.mix])}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-slate-600">{row.provider}</td>
                            <td className="px-4 py-4 text-slate-600">{row.serviceType}</td>
                            <td className="px-4 py-4 text-right font-semibold text-ink">{formatCurrency(row.billed)}</td>
                            <td className="px-4 py-4 text-right text-slate-600">{formatCurrency(row.paid)}</td>
                            <td className="px-4 py-4 text-right font-semibold text-rose-700">{formatCurrency(row.balance)}</td>
                            <td className="px-4 py-4 text-center">
                              <div className="font-semibold text-ink">{row.ageDays}</div>
                              <div className="text-[11px] text-slate-400">
                                {row.pastDueDays > 0 ? `${row.pastDueDays} ${tr('past due')}` : tr('current')}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="font-semibold text-ink">{formatPercent(row.riskScore)}</div>
                              <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${riskTone[row.riskBand]}`}>
                                {row.riskBand}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-slate-600">
                              <div>{row.forecastDate ? formatDate(row.forecastDate) : tr('Settled')}</div>
                              <div className="text-[11px] text-slate-400">{formatPercent(row.collectionProbability)} {tr('likely')}</div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusTone[row.status]}`}>
                                {row.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              {selectedRow ? (
                <div className="space-y-5">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">{tr('Focused invoice')}</div>
                    <h2 className="mt-2 text-xl font-semibold text-ink">{selectedRow.invoiceNumber}</h2>
                    <p className="text-sm text-slate-500">
                      {selectedRow.patient} • {selectedRow.payer}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <MetricLine label={tr('Provider')} value={selectedRow.provider} />
                    <MetricLine label={tr('Service type')} value={selectedRow.serviceType} />
                    <MetricLine label={tr('Invoice date')} value={formatDate(selectedRow.invoiceDate)} />
                    <MetricLine label={tr('Due date')} value={formatDate(selectedRow.dueDate)} />
                    <MetricLine label={tr('Billed')} value={formatCurrency(selectedRow.billed)} />
                    <MetricLine label={tr('Paid')} value={formatCurrency(selectedRow.paid)} />
                    <MetricLine label={tr('Outstanding')} value={formatCurrency(selectedRow.balance)} />
                    <MetricLine label={tr('Age')} value={`${selectedRow.ageDays} ${tr('days')}`} />
                    <MetricLine label={tr('Past due')} value={`${selectedRow.pastDueDays} ${tr('days')}`} />
                    <MetricLine label={tr('Recovery risk')} value={formatPercent(selectedRow.riskScore)} detail={selectedRow.riskBand} />
                    <MetricLine label={tr('Collection probability')} value={formatPercent(selectedRow.collectionProbability)} />
                    <MetricLine label={tr('Forecast date')} value={selectedRow.forecastDate ? formatDate(selectedRow.forecastDate) : tr('Settled')} />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <StatCard title={tr('Payments logged')} value={String(selectedRow.paymentCount)} subtitle={selectedRow.lastPaymentDate ? formatDate(selectedRow.lastPaymentDate) : tr('No payment yet')} />
                    <StatCard title={tr('Billing lines')} value={String(selectedRow.itemCount)} subtitle={selectedRow.paymentMethods.length ? selectedRow.paymentMethods.join(', ') : tr('No payment method yet')} />
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                      <ArrowUpRight size={16} />
                      {tr('Suggested next move')}
                    </div>
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      {selectedRow.balance <= 0
                        ? tr('This invoice is already settled. Use export to capture it in the reporting package.')
                        : selectedRow.pastDueDays > 60
                        ? tr('Prioritize immediate follow-up, confirm payer status, and route to collections review.')
                        : selectedRow.riskBand === 'high'
                        ? tr('Review payer rules and recent payment activity before this balance ages further.')
                        : tr('Monitor normally and keep payment reminders aligned with the forecast window.')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500">{tr('Select an invoice to inspect its live recovery profile.')}</div>
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 text-sm font-semibold text-ink">{title}</div>
      <div className="min-h-[320px]">{children}</div>
    </section>
  );
}

function MetricLine({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 text-sm">
      <div className="text-slate-500">{label}</div>
      <div className="text-right">
        <div className="font-semibold text-ink">{value}</div>
        {detail ? <div className="text-xs capitalize text-slate-400">{detail}</div> : null}
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle }: { title: string; value: string; subtitle: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-400">{title}</div>
      <div className="mt-2 text-2xl font-semibold text-ink">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
    </div>
  );
}
